import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Droplets, Heart, Sprout, Download, Ticket } from 'lucide-react';
import { AlmondIcon } from '../components/AlmondIcon';
import Hero from '../components/home/Hero';
import useReveal from '../hooks/useReveal';
import { products, images, mission, secretRecipe, blogPosts, specials } from '../mock/mock';

const circleColors = {
  'almond-8': '#EAD9BE',
  'almond-20': '#D8BC9B',
  walnut: '#AEBE8E',
  hazelnut: '#E6C3A6',
};
import ReviewsSection from '../components/reviews/ReviewsSection';
import { useContent, resolveImg } from '../context/ContentContext';

const infoBarWords = [
  'Freshly made', 'Activated nuts', 'Small batch', 'No gums or oil',
  'No preservatives', 'Locally sourced',
];

function InfoBar() {
  const loop = [...infoBarWords, ...infoBarWords];
  return (
    <div className="bg-[#E9E5D8] text-[#2C3A28] border-y-2 border-[#2C3A28]/10 overflow-hidden py-3.5">
      <div className="flex w-max animate-marquee">
        {loop.map((w, i) => (
          <span key={i} className="flex items-center shrink-0">
            <span className="text-[11px] sm:text-[13px] font-bold uppercase tracking-[0.2em] whitespace-nowrap px-6 sm:px-8">
              {w}
            </span>
            <AlmondIcon size={14} className="text-[#2C3A28] shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home({ onNewsletter, onOrder, onPickup }) {
  const { content, img, txt } = useContent();
  const t = content.text || {};
  useReveal([]);
  const stepIcons = [Sprout, Droplets, AlmondIcon, Heart];

  return (
    <div>
      <Hero onOrder={onOrder} onPickup={onPickup} />
      <InfoBar />

      {/* PRODUCTS */}
      <section className="py-24 sm:py-32 bg-[#E9EBED]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
                {t.rangeEyebrow || 'Our range'}
              </span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
                {t.rangeHeading || 'Four milks, endlessly pure'}
              </h2>
            </div>
            <Link
              to="/products"
              className="group inline-flex items-center gap-2 text-[#2C3A28] font-semibold hover:text-[#2C3A28] transition-colors"
            >
              {t.rangeLink || 'Build your box'}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {products.map((p, i) => (
              <Link
                to={`/products#${p.id}`}
                key={p.id}
                className="reveal group relative flex flex-col items-center text-center transition-transform duration-500 hover:-translate-y-2"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative w-full flex items-end justify-center h-80 lg:h-[32rem]">
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-56 h-56 lg:w-72 lg:h-72 rounded-full transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundColor: circleColors[p.id] || p.tone }}
                  />
                  <img
                    src={p.image}
                    alt={`${p.name} ${p.badge}`}
                    className="relative h-full object-contain drop-shadow-[0_18px_22px_rgba(0,0,0,0.18)] transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span
                    className="absolute bottom-1 left-1 lg:left-3 w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center font-display text-xl lg:text-2xl text-[#EDEAE0] shadow-md"
                    style={{ backgroundColor: '#5E4632' }}
                  >
                    {p.badge.split(' ')[0]}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold text-[#2C3A28]">{p.name}</h3>
                <p className="text-sm text-[#3B2A20]/60">{p.tagline}</p>
                <p className="mt-3 font-semibold" style={{ color: p.accent }}>
                  from ${p.price.toFixed(2)}
                </p>
              </Link>
            ))}
          </div>

          <div className="reveal mt-14 flex justify-center">
            <Link
              to="/products"
              data-testid="range-shop-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-8 py-4 font-semibold hover:bg-[#1f2a1c] transition-all hover:-translate-y-0.5"
            >
              Shop the range
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* MOBILE-ONLY: VISIT CAFE CTA (after products) */}
      <section className="sm:hidden bg-[#E9EBED] px-5 pb-10 -mt-6">
        <Link
          to="/cafe"
          data-testid="mobile-cafe-cta"
          className="reveal block relative rounded-[1.75rem] overflow-hidden shadow-lg"
        >
          <img
            src={img('cafeInterior', images.cafeInterior)}
            alt="Al Naturale café in St Kilda"
            className="w-full h-72 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C3A28] via-[#2C3A28]/55 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-[#E9EBED]">
            <span className="text-[#2C3A28] text-xs font-semibold uppercase tracking-[0.22em]">
              Visit us
            </span>
            <h3 className="mt-2 font-display text-3xl font-semibold leading-tight">
              Come say ciao in St Kilda
            </h3>
            <p className="mt-2 text-[#EDEAE0]/85 text-sm leading-relaxed">
              Fresh nut-milk coffee & all-day Italian brunch at 6 Acland Street.
            </p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-6 py-3 font-semibold text-sm">
              Visit the café
              <ArrowUpRight size={16} />
            </span>
          </div>
        </Link>
      </section>

      {/* SECRET RECIPE */}
      <section className="relative py-24 sm:py-32 bg-[#2C3A28] text-[#EDEAE0] overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal order-2 lg:order-1">
            <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              How it's made
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
              {secretRecipe.title}
            </h2>
            <p className="mt-6 text-white text-lg leading-relaxed">{secretRecipe.body}</p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {secretRecipe.steps.map((s, i) => {
                const Icon = stepIcons[i];
                return (
                  <div
                    key={s.n}
                    className="rounded-2xl bg-[#EDEAE0]/6 border border-[#EDEAE0]/12 p-5 hover:bg-[#EDEAE0]/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={22} className="text-[#2C3A28]" />
                      <span className="font-display text-2xl text-[#EDEAE0]/30">{s.n}</span>
                    </div>
                    <p className="mt-3 font-display text-xl font-bold text-[#EDEAE0]">{s.t}</p>
                    <p className="mt-1 text-sm text-white leading-relaxed">{s.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="reveal order-1 lg:order-2">
            <img
              src={img('pour', images.pour)}
              alt="Pouring fresh nut milk"
              className="rounded-[2rem] w-full h-[560px] object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* CAFE INVITE band */}
      <section className="relative py-28 sm:py-36 overflow-hidden">
        <img
          src={img('experienceBg', 'https://customer-assets-0z36b82j.emergentagent.net/job_fresh-italiano/artifacts/bu9e9vdu_image.webp')}
          alt="Al Naturale café interior in St Kilda"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/45 to-black/10" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center reveal">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase leading-snug text-[#E9E5D8] [text-shadow:0_2px_16px_rgba(0,0,0,0.85)]">
            {txt('home', 'experienceHeading', t.experienceHeading || 'Come and experience the story')}
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-[#EDEAE0] text-lg leading-relaxed [text-shadow:0_1px_12px_rgba(0,0,0,0.9)]">
            {txt('home', 'experienceBody', t.experienceBody || 'Visit our café on Acland Street and discover our range of fresh food and drinks.')}
          </p>
          <Link
            to="/menu"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-4 font-semibold hover:bg-[#1f2a1c] transition-colors shadow-lg"
          >
            {t.experienceBtn || 'View our menu'}
          </Link>
        </div>
      </section>


      {/* ORDER ONLINE CTA */}
      <section className="relative bg-[#2C3A28] text-[#EDEAE0] overflow-hidden grain">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
          <div className="reveal grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-center">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white">
                {t.orderEyebrow || 'Order online'}
              </span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase leading-[0.98] text-[#E9E5D8]">
                {t.orderHeading1 || 'Freshly made.'}<br />
                <span className="text-[#E9E5D8]">{t.orderHeading2 || 'Ready when you are.'}</span>
              </h2>
              <p className="mt-6 max-w-md text-white text-lg leading-relaxed">
                {t.orderSub || 'All day breakfast or lunch.'}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <button
                  onClick={onOrder}
                  data-testid="home-order-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#E9E5D8] text-[#2C3A28] px-8 py-4 font-semibold transition-all hover:-translate-y-0.5"
                >
                  {t.orderBtn || 'Order Online'}
                  <ArrowUpRight size={19} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <div className="flex items-center gap-4 text-sm text-white">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#EB1700]" /> DoorDash
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#06C167]" /> Uber Eats
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: t.card1Label || 'Ready in', v: t.card1Value || '10-15 min', s: t.card1Sub || 'for pick-up' },
                { k: t.card2Label || 'Delivery', v: t.card2Value || '25-35 min', s: t.card2Sub || 'to your door' },
                { k: t.card3Label || 'Hours', v: t.card3Value || '7am to 3pm', s: t.card3Sub || 'every day' },
              ].map((c) => (
                <div
                  key={c.k}
                  className="rounded-2xl border border-[#EDEAE0]/12 bg-[#EDEAE0]/[0.06] px-5 py-6 hover:bg-[#EDEAE0]/10 transition-colors"
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] text-white">{c.k}</p>
                  <p className="mt-2 font-display text-2xl font-semibold text-white">{c.v}</p>
                  <p className="mt-1 text-sm text-white">{c.s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsSection heading={t.reviewsHeading || 'What people are saying'} eyebrow={t.reviewsEyebrow || 'Loved in Melbourne'} />

      {/* SPECIALS */}
      <section className="pt-8 pb-24 sm:pt-10 sm:pb-32 bg-[#EDEAE0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal order-2 lg:order-1">
            <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              {t.specialsEyebrow || 'This month'}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              {t.specialsHeading || 'Our latest specials'}
            </h2>
            <p className="mt-6 text-[#2C3A28] text-lg leading-relaxed max-w-lg">
              {t.specialsBody || 'Lunch specials, take-home meals, toastie & coffee deals and more. Download our specials booklet, or grab a 10% off voucher to use in the cafe.'}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={specials.bookletUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-4 font-semibold hover:bg-[#2C3A28] transition-all hover:-translate-y-0.5"
              >
                <Download size={18} /> {t.specialsBookletBtn || 'Get my specials booklet'}
              </a>
              <a
                href={specials.voucherUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#2C3A28]/25 text-[#2C3A28] px-7 py-4 font-semibold hover:bg-[#E9EBED] transition-colors"
              >
                <Ticket size={18} /> {t.specialsVoucherBtn || 'Get my 10% off voucher'}
              </a>
            </div>
          </div>
          <div className="reveal order-1 lg:order-2">
            <a
              href={specials.bookletUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block group relative rounded-[2rem] overflow-hidden shadow-xl bg-white"
            >
              <img
                src={resolveImg(t.specialsImage) || specials.image}
                alt="Al Naturale specials booklet"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-[#EDEAE0] px-5 py-3 text-sm font-semibold shadow-lg">
                <Download size={16} /> View booklet
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="pt-20 sm:pt-24 pb-24 sm:pb-32 bg-[#E9EBED]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal flex items-end justify-between gap-6">
            <div>
              <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
                {t.blogEyebrow || 'From the journal'}
              </span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
                {t.blogHeading || 'Welcome to our blog'}
              </h2>
            </div>
            <Link
              to="/blog"
              className="group hidden sm:inline-flex items-center gap-2 text-[#2C3A28] font-semibold hover:text-[#2C3A28] transition-colors"
            >
              {t.blogLink || 'Read all'}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {(content.posts && content.posts.length ? content.posts : blogPosts).slice(0, 3).map((b, i) => (
              <Link
                to="/blog"
                key={b.id}
                className="reveal group rounded-3xl overflow-hidden bg-[#EDEAE0] hover:-translate-y-1.5 transition-transform duration-400"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={resolveImg(b.image)}
                    alt={b.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#2C3A28] font-semibold">
                    {b.tag} · {b.read}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-semibold text-[#2C3A28] leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-[#2C3A28] leading-relaxed line-clamp-2">{b.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
